import { translate } from '@thoughtspot/i18n';
import { hexToRgbA } from '@thoughtspot/radiant-react/widgets/color';
import _ from 'lodash';
import { ANOMALY_BAR_HEIGHT, ANOMALY_GRAY_TINT4_COLOR, EXPECTED_ANOMALY_CONSTANTS, getForecastDetailsWithExpectedColor, getSeriesData, isShowAnomaliesEnabled, isShowAnomaliesSupported, } from '../components/kpi/cortex-utils';
import { isInCompleteKPI } from '../components/kpi/kpi-utils';
import { isCustomCalendarTimeSeries } from '../core/chart-model-utils';
import { LEGEND_FONT_FAMILY } from '../theme/chart-theme-service';
const DELAY_ONE_MIN = 60 * 1000;
function setColorToPoint(point, color) {
    _.set(point, 'color', color);
}
function isDataEmpty(dataModel) {
    var _a;
    const series = getSeriesData(dataModel);
    const data = Array.isArray(series) && ((_a = series[0]) === null || _a === void 0 ? void 0 : _a.data);
    const size = Array.isArray(data) && _.size(data);
    return size === 0;
}
function hasMarkerAndEnabled(point) {
    var _a;
    return _.has(point, 'marker') && ((_a = point.marker) === null || _a === void 0 ? void 0 : _a.enabled);
}
function getExpectationTextAndColor(chart, chartModel, dataModel) {
    const isCustomCalendarTimeSeriesChart = isCustomCalendarTimeSeries(chartModel);
    const { color, labelColor, label, lastPoint, data, size, isLoading, } = getForecastDetailsWithExpectedColor(chartModel, chart.series[0].color, dataModel, isCustomCalendarTimeSeriesChart);
    setColorToPoint(data[size - 1], color);
    return {
        color,
        labelColor,
        label,
        lastPoint,
        isLoading,
    };
}
function drawVerticalLine(chartInstance, options = {}) {
    const { color = 'red', strokeWidth = 1.5, barHeight } = options;
    const series = chartInstance.series[0];
    if (!series || series.points.length === 0) {
        return;
    }
    const lastPoint = series.points[series.points.length - 1];
    if (!lastPoint)
        return;
    if (chartInstance.customVerticalLine) {
        chartInstance.customVerticalLine.destroy();
    }
    const lineLeftShift = 0.5;
    const lineExtension = barHeight;
    chartInstance.customVerticalLine = chartInstance.renderer
        .path([
        'M',
        lastPoint.plotX + chartInstance.plotLeft - lineLeftShift,
        lastPoint.plotY + chartInstance.plotTop,
        'L',
        lastPoint.plotX + chartInstance.plotLeft - lineLeftShift,
        chartInstance.plotHeight + chartInstance.plotTop + lineExtension,
    ])
        .attr({
        stroke: color,
        'stroke-width': strokeWidth,
    })
        .add();
}
const drawBottomBarAndLabel = (chartInstance, options = {}) => {
    const { color = 'red', label = '', labelColor = '', barHeight, } = options;
    const series = chartInstance.series[0];
    if (!series || series.points.length === 0) {
        return;
    }
    const lastPoint = series.points[series.points.length - 1];
    if (!lastPoint)
        return;
    const firstPoint = series.points[0];
    const firstPointX = chartInstance.plotLeft + firstPoint.plotX;
    const lastPointX = chartInstance.plotLeft + lastPoint.plotX;
    if (chartInstance.anomalyV2BottomBar) {
        chartInstance.anomalyV2BottomBar.destroy();
    }
    if (chartInstance.anomalyV2TopBorder) {
        chartInstance.anomalyV2TopBorder.destroy();
    }
    if (chartInstance.anomalyV2BottomBarLabel) {
        chartInstance.anomalyV2BottomBarLabel.destroy();
    }
    chartInstance.anomalyV2BottomBar = chartInstance.renderer
        .rect(firstPointX, chartInstance.plotTop + chartInstance.plotHeight, lastPointX - firstPointX, barHeight)
        .attr({
        fill: hexToRgbA(color, 0.2),
        stroke: 'none',
        zIndex: 5,
    })
        .add();
    let borderLastPoint = lastPointX - 2;
    if (hasMarkerAndEnabled(lastPoint) &&
        Math.floor(lastPoint.plotY) + 3 >= chartInstance.plotHeight) {
        borderLastPoint = lastPointX - 6;
    }
    chartInstance.anomalyV2TopBorder = chartInstance.renderer
        .path([
        'M',
        firstPointX,
        chartInstance.plotTop + chartInstance.plotHeight,
        'L',
        borderLastPoint,
        chartInstance.plotTop + chartInstance.plotHeight,
    ])
        .attr({
        stroke: ANOMALY_GRAY_TINT4_COLOR,
        'stroke-width': 1,
        zIndex: 6,
    })
        .add();
    chartInstance.anomalyV2BottomBarLabel = chartInstance.renderer
        .text(translate(label), lastPointX - 10, chartInstance.plotTop +
        chartInstance.plotHeight +
        barHeight / 2 +
        5)
        .attr({
        align: 'right',
        zIndex: 6,
    })
        .css({
        color: labelColor,
        fontSize: '12px',
        fontWeight: '400',
        lineHeight: '18px',
        fontFamily: LEGEND_FONT_FAMILY,
        textAlign: 'center',
    })
        .add();
};
export const addAnomalyExpectationBarAndLine = (chart, chartModel, dataModel) => {
    if (!isShowAnomaliesSupported(chartModel) || isDataEmpty(dataModel)) {
        return;
    }
    const { color, labelColor, label, lastPoint, isLoading, } = getExpectationTextAndColor(chart, chartModel, dataModel);
    const options = {
        color,
        label,
        labelColor,
        lastPoint,
        barHeight: ANOMALY_BAR_HEIGHT,
    };
    if (isInCompleteKPI(chartModel) || !isShowAnomaliesEnabled(chartModel)) {
        return;
    }
    drawVerticalLine(chart, options);
    drawBottomBarAndLabel(chart, options);
    if (isLoading) {
        _.delay(drawBottomBarAndLabel, DELAY_ONE_MIN, chart, {
            ...options,
            label: EXPECTED_ANOMALY_CONSTANTS.CAN_NOT_DETECT,
        });
    }
};
export const drawAnomalyV2 = (chart) => {
    addAnomalyExpectationBarAndLine(chart.getHighchartObj(), chart.chartModel, chart.dataModel);
};
//# sourceMappingURL=secondary-render-util.js.map